import moment from "moment"
import { useEffect, useState } from "react"
import { Box, Button, Flex, Heading, Text } from "rebass/styled-components"
import { Menu as MenuInner, MenuItem, MenuButton, FocusableItem, MenuDivider } from '@szhsin/react-menu';
import {
  menuSelector,
  menuItemSelector,
  menuDividerSelector
} from "@szhsin/react-menu/style-utils";
import '@szhsin/react-menu/dist/core.css';
import CalendarGridMini from "../../../dashboard/CalendarGridMini"
import styled, { keyframes } from "styled-components";
import PleaseWait from "../../../../components/PleaseWait";

const menuShow = keyframes`
  from {
    opacity: 0;
  }
`;
const menuHide = keyframes`
  to {
    opacity: 0;
  }
`;
export const Menu = styled(MenuInner)`
  ${menuSelector.name} {
    font-size: 0.925rem;
    user-select: none;
    box-shadow: 1px 1px 20px 1px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 6px;
    min-width: 10rem;
  }

  ${menuSelector.stateOpening} {
    animation: ${menuShow} 0.15s ease-out;
  }

  // NOTE: animation-fill-mode: forwards is required to
  // prevent flickering with React 18 createRoot()
  ${menuSelector.stateClosing} {
    animation: ${menuHide} 0.2s ease-out forwards;
  }

  ${menuItemSelector.name} {
    border-radius: 6px;
    padding: 0.375rem 0.625rem;
  }

  ${menuItemSelector.hover} {
    color: #fff;
    background-color: #59a2ff;
  }

  ${menuDividerSelector.name} {
    margin: 0.5rem 0.625rem;
  }

  ${menuItemSelector.submenu} {
    position: relative;
    &::after {
      position: absolute;
      width: 7px;
      right: 0.625rem;
    }
  }
`;

interface IProps {
  isSaving: boolean,
  onCancel: () => void
  onSave: (date: string) => void
}
export const SnoozePick: React.FC<IProps> = ({ isSaving, onCancel, onSave }) => {
  const [selectedDateSnooze, setSelectedDateSnooze] = useState(moment().format())
  const [selectedTimeSnooze, setSelectedTimeSnooze] = useState('')
  const [final, setFinal] = useState('')

  const roundClosest15 = () => {
    const start = moment();
    const remainder = 15 - (start.minute() % 15);

    const dateTime = moment(start).add(remainder, "minutes").format("hh:mm:A");
    setSelectedTimeSnooze(dateTime)
  }

  const computeFinal = () => {
    let f = `${moment(selectedDateSnooze).format('MM-DD-YYYY')}T${getHours()}:${getMinutes()}${getA()}`
    setFinal(f)
  }

  useEffect(() => {
    roundClosest15()
    computeFinal()
  }, [])

  useEffect(() => {
    computeFinal()
  }, [selectedDateSnooze, selectedTimeSnooze])

  const getHours = () => {
    const sp = selectedTimeSnooze.split(':')
    return sp[0]
  }

  const getMinutes = () => {
    const sp = selectedTimeSnooze.split(':')
    return sp[1]
  }

  const getA = () => {
    const sp = selectedTimeSnooze.split(':')
    return sp[2]
  }

  const setHours = (hour: string | number) => {
    let sp = selectedTimeSnooze.split(':')
    sp[0] = hour.toString()
    setSelectedTimeSnooze(sp.join(':'))
  }

  const setMinutes = (minute: string | number) => {
    let sp = selectedTimeSnooze.split(':')
    sp[1] = minute.toString()
    setSelectedTimeSnooze(sp.join(':'))
  }

  const setA = (a: string | number) => {
    let sp = selectedTimeSnooze.split(':')
    sp[2] = a.toString()
    setSelectedTimeSnooze(sp.join(':'))
  }

  return (
    <Box style={{ minWidth: '400px', position: 'relative' }}>
      <Text fontSize={24} fontWeight={500}>Pick date & time</Text>
      <Box mt={2} style={{ width: '100%', height: '1px', background: 'rgb(200, 213, 235)' }} />
      <Flex>
        <CalendarGridMini selectedDate={selectedDateSnooze} setSelectedDate={(d) => { setSelectedDateSnooze(d || moment().format()) }} />
        <Flex flexDirection={'column'} style={{ width: '200px' }} ml={4}>
          <Flex mt={6} style={{ position: 'relative', height: 'fit-content', width: 'fit-content' }}>
            <Menu menuStyle={{
              width: 'fit-content'
            }} transition menuButton={
              <Text style={{ cursor: "pointer" }}>{getHours()}</Text>
            }>
              {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(x => {
                return (
                  <MenuItem key={x} style={{ marginTop: '4px', backgroundColor: getHours() === x.toString() ? 'rgb(200, 213, 235)' : '' }} onClick={async () => {
                    setHours(x)
                  }}>
                    <Text fontWeight={400} flex={1}>{x}</Text>
                  </MenuItem>
                )
              })}
            </Menu>
            <Text fontWeight={400} flex={1}>:</Text>
            <Menu menuStyle={{
              width: 'fit-content'
            }} transition menuButton={
              <Text style={{ cursor: "pointer" }}>{getMinutes()}</Text>
            }>
              {['00', '15', '30', '45'].map(x => {
                return (
                  <MenuItem key={x} style={{ marginTop: '4px', backgroundColor: getMinutes() === x ? 'rgb(200, 213, 235)' : '' }} onClick={async () => {
                    setMinutes(x)
                  }}>
                    <Text fontWeight={400} flex={1}>{x}</Text>
                  </MenuItem>
                )
              })}
            </Menu>
            <Text fontWeight={400} flex={1}>:</Text>
            <Menu menuStyle={{
              width: 'fit-content'
            }} transition menuButton={
              <Text style={{ cursor: "pointer" }}>{getA()}</Text>
            }>
              {['AM', 'PM'].map(x => {
                return (
                  <MenuItem key={x} style={{ marginTop: '4px', backgroundColor: getA() === x ? 'rgb(200, 213, 235)' : '' }} onClick={async () => {
                    setA(x)
                  }}>
                    <Text fontWeight={400} flex={1}>{x}</Text>
                  </MenuItem>
                )
              })}
            </Menu>
          </Flex>
          <Box mt={2} style={{ width: '100%', height: '1px', background: 'rgb(200, 213, 235)' }} />
          {!final || moment(final, 'MM-DD-YYYYThh:mmA').isBefore(moment()) ? (
            <Text mt={2} style={{ color: 'red', fontWeight: 600, fontSize: '12px' }}>Selected date and time cannot be in the past</Text>
          ) : null}
        </Flex>

      </Flex>
      <Flex justifyContent={'flex-end'}>
        <Button onClick={onCancel} variant={'white'} style={{ width: 'fit-content' }}>Cancel</Button>
        <Button disabled={!!isSaving || !final || moment(final, 'MM-DD-YYYYThh:mmA').isBefore(moment())} variant={'primary'}
          onClick={() => onSave(final)}
        >Save</Button>
      </Flex>
      {isSaving && (
        <Flex justifyContent={'center'} alignItems={'center'} style={{ position: 'absolute', top: 0, right: 0, width: '100%', height: '100%', background: 'rgba(255,255,255, 0.6)' }}>
          <PleaseWait />
        </Flex>
      )}
      {/* <Calendar value={null} onChange={(d)=>{}} formatShortWeekday={(locale, date) => moment(date).format('dd')}/> */}
    </Box>)
}

